import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  category: string;
  type: "merchandise" | "equipment" | "music";
  source?: "local" | "gp"; // "local" for tenant's own products, "gp" for GP products (defaults to "local")
  gpProductId?: string; // GP's product ID if source is "gp"
  previewUrl?: string; // Audio preview URL for music items
  isDigital?: boolean; // True for music downloads
  sku?: string; // Product SKU
  variant_id?: string; // Variant ID if purchasing a specific variant
  variant_name?: string; // Display name for the variant (e.g., "Large / Red")
  recipient_name?: string; // Optional "for X" label so a single buyer can label items for different people (e.g., gifts, multi-athlete households). Free-text — not an athlete FK because gifts/non-members need to work.
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateRecipient: (itemId: string, recipientName: string) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
  getLocalItems: () => CartItem[];
  getGPItems: () => CartItem[];
  hasGPItems: () => boolean;
  hasMusicItems: () => boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    // safeGetItem, not localStorage.getItem: reading the property itself throws
    // a SecurityError inside an iframe on a browser blocking third-party
    // storage (Safari's default), and this runs in a useState initializer at
    // the app root — so the throw lands during the very first render and the
    // top-level error boundary replaces the whole page. That is a blank
    // registration form on every /embed/* route
    // (CLUB-CENTRAL-TENANT-4P / -4M). The try/catch below only ever guarded
    // JSON.parse; the throw happens before it.
    const savedCart = safeGetItem("cart");
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        // Migrate old cart items that don't have source field
        return parsed.map((item: CartItem) => ({
          ...item,
          source: item.source || "local",
        }));
      } catch {
        return [];
      }
    }
    return [];
  });

  // Save to localStorage whenever cart changes. safeSetItem for the same
  // reason as the read above — a blocked-storage iframe must lose the cart,
  // not the page.
  useEffect(() => {
    safeSetItem("cart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (item: Omit<CartItem, "quantity">, quantity: number = 1) => {
    setCartItems((prev) => {
      // Generate unique key using source, id, and variant_id to differentiate products
      const itemKey = `${item.source || "local"}-${item.id}-${item.variant_id || ""}`;
      const existingItem = prev.find(
        (i) => `${i.source || "local"}-${i.id}-${i.variant_id || ""}` === itemKey
      );

      if (existingItem) {
        return prev.map((i) =>
          `${i.source || "local"}-${i.id}-${i.variant_id || ""}` === itemKey
            ? { ...i, quantity: i.quantity + quantity }
            : i
        );
      }

      // Add source default if not provided
      const newItem = {
        ...item,
        quantity,
        source: item.source || "local",
      } as CartItem;

      return [...prev, newItem];
    });
  };

  const removeFromCart = (itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, quantity } : item))
    );
  };

  const updateRecipient = (itemId: string, recipientName: string) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? { ...item, recipient_name: recipientName.trim() || undefined }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getLocalItems = () => {
    return cartItems.filter((item) => item.source === "local");
  };

  const getGPItems = () => {
    return cartItems.filter((item) => item.source === "gp");
  };

  const hasGPItems = () => {
    return cartItems.some((item) => item.source === "gp");
  };

  const hasMusicItems = () => {
    return cartItems.some((item) => item.type === "music");
  };

  const value: CartContextType = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateRecipient,
    clearCart,
    getTotalItems,
    getTotalPrice,
    getLocalItems,
    getGPItems,
    hasGPItems,
    hasMusicItems,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
